import React from "react";
import { useNavigate } from "react-router-dom";
import { Play, Pause, SkipBack, SkipForward, X, Volume2, RotateCcw, RotateCw, Gauge, Moon, Loader2 } from "lucide-react";
import { usePlayer } from "../context/PlayerContext";
import { useLang } from "../context/LanguageContext";
import { formatTime } from "../mock";
import { Slider } from "./ui/slider";
import BookCover from "./BookCover";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator,
} from "./ui/dropdown-menu";

const SPEEDS = [0.8, 1, 1.25, 1.5, 1.75, 2];
const SLEEP_OPTIONS = [5, 10, 15, 30, 45, 60];

export default function AudioPlayer() {
  const {
    current, isPlaying, progress, duration, volume, rate, sleepMinutes, sleepRemaining,
    buffering, error, needsResume,
    setVolume, setRate, setSleepMinutes, togglePlay, seek, skip, playNext, playPrev, closePlayer,
  } = usePlayer();
  const { t } = useLang();
  const navigate = useNavigate();

  if (!current) return null;
  const { book, chapter, isSample } = current;
  const title = book.title_en;
  // The second line carries state when there is state to carry: a stall the
  // player could not recover from matters more than the chapter name.
  const subtitle = error === "stalled"
    ? "Playback stopped. Press play to retry"
    : buffering
      ? "Loading…"
      : needsResume
        ? "Paused. Press play to pick up where you left off"
        : isSample
          ? "Free sample"
          : chapter?.title_en || "";

  return (
    <div className="fixed bottom-0 inset-x-0 z-50 bg-card/95 backdrop-blur-xl border-t border-border">
      <div className="max-w-[1400px] mx-auto px-3 sm:px-6">
        {/* progress bar */}
        <div className="pt-2">
          <Slider
            value={[progress]}
            max={duration || 100}
            step={1}
            onValueChange={(v) => seek(v[0])}
            aria-label="Seek"
            className="cursor-pointer"
          />
        </div>
        <div className="flex items-center gap-3 sm:gap-4 h-[68px]">
          {/* track info */}
          <div
            className="flex items-center gap-3 min-w-0 w-[30%] cursor-pointer"
            onClick={() => navigate(`/book/${book.slug || book.id}`)}
          >
            <BookCover book={book} className="w-11 h-11 rounded-md shrink-0 cover-shadow" />
            <div className="min-w-0 hidden sm:block">
              <p className="text-sm font-semibold truncate">{title}</p>
              <p className="text-xs text-muted-foreground truncate">{subtitle}</p>
            </div>
          </div>

          {/* controls */}
          <div className="flex items-center gap-2 sm:gap-3 mx-auto">
            <button onClick={() => skip(-15)} aria-label="Back 15 seconds" className="text-muted-foreground hover:text-foreground transition-colors" title="-15s">
              <RotateCcw className="w-5 h-5" />
            </button>
            <button onClick={playPrev} aria-label="Previous chapter" className="hidden sm:block text-muted-foreground hover:text-foreground transition-colors" title="Previous chapter">
              <SkipBack className="w-5 h-5" />
            </button>
            <button
              onClick={togglePlay}
              aria-label={isPlaying ? "Pause" : "Play"}
              className="w-11 h-11 rounded-full bg-brand-500 hover:bg-brand-400 text-brand-950 flex items-center justify-center transition-colors shadow-lg focus-visible:ring-2 focus-visible:ring-brand-200"
            >
              {buffering
                ? <Loader2 className="w-5 h-5 animate-spin" />
                : isPlaying
                  ? <Pause className="w-5 h-5" fill="currentColor" />
                  : <Play className="w-5 h-5 ml-0.5" fill="currentColor" />}
            </button>
            <button onClick={playNext} aria-label="Next chapter" className="hidden sm:block text-muted-foreground hover:text-foreground transition-colors" title="Next chapter">
              <SkipForward className="w-5 h-5" />
            </button>
            <button onClick={() => skip(15)} aria-label="Forward 15 seconds" className="text-muted-foreground hover:text-foreground transition-colors" title="+15s">
              <RotateCw className="w-5 h-5" />
            </button>
          </div>

          {/* time + speed + sleep + volume + close */}
          <div className="flex items-center gap-2 sm:gap-3 w-[34%] justify-end">
            <span className="hidden sm:block text-xs text-muted-foreground tabular-nums">
              {formatTime(progress)} / {formatTime(duration)}
            </span>

            {/* speed control */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="hidden sm:flex items-center gap-1 h-8 px-2 rounded-full border border-border text-xs font-semibold text-muted-foreground hover:text-foreground hover:border-brand-500/50 transition-colors"
                  title={"Speed"}
                >
                  <Gauge className="w-3.5 h-3.5" /> {rate}x
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>{"Speed"}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {SPEEDS.map((s) => (
                  <DropdownMenuItem key={s} onClick={() => setRate(s)} className={rate === s ? "text-brand-400" : ""}>
                    {s}x
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* sleep timer */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className={`hidden sm:flex items-center gap-1 h-8 px-2 rounded-full border text-xs font-semibold transition-colors ${
                    sleepMinutes
                      ? "border-brand-500/60 text-brand-400"
                      : "border-border text-muted-foreground hover:text-foreground hover:border-brand-500/50"
                  }`}
                  title={"Sleep timer"}
                >
                  <Moon className="w-3.5 h-3.5" />
                  {sleepMinutes ? formatTime(sleepRemaining) : ""}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>{"Sleep after"}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {SLEEP_OPTIONS.map((m) => (
                  <DropdownMenuItem key={m} onClick={() => setSleepMinutes(m)} className={sleepMinutes === m ? "text-brand-400" : ""}>
                    {m} min
                  </DropdownMenuItem>
                ))}
                {sleepMinutes > 0 && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => setSleepMinutes(0)} className="text-red-400 focus:text-red-400">
                      {"Cancel"}
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>

            <div className="hidden lg:flex items-center gap-2 w-24">
              <Volume2 className="w-4 h-4 text-muted-foreground shrink-0" />
              <Slider value={[volume * 100]} max={100} step={1} aria-label="Volume" onValueChange={(v) => setVolume(v[0] / 100)} />
            </div>
            <button onClick={closePlayer} aria-label="Close player" className="text-muted-foreground hover:text-foreground transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
